import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Landmark, Lock } from "lucide-react";
import { LogoLockup } from "./Logo";
import { scrollToId } from "../lib/app";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/programs", label: "Programs" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const goEnroll = () => {
    setOpen(false);
    navigate("/programs");
    setTimeout(() => scrollToId("register"), 400);
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50" data-testid="site-header">
      <div className="bg-navy-950 border-b border-gold-500/20 text-[10.5px] sm:text-[11px] font-mono tracking-[0.14em] text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 flex items-center justify-between">
          <span className="flex items-center gap-2" data-testid="gov-banner">
            <Landmark className="w-3.5 h-3.5 text-gold-400" />
            AN OFFICIAL SHOWCASE OF U.S. FEDERAL AGENCIES
          </span>
          <span className="hidden md:flex items-center gap-5">
            <span className="flex items-center gap-1.5 text-gold-300">
              <Lock className="w-3 h-3" /> HTTPS / TLS 1.3
            </span>
            <span className="hidden lg:inline">FOIA</span>
            <span className="hidden lg:inline">ACCESSIBILITY (508)</span>
            <span className="hidden lg:inline">VULNERABILITY DISCLOSURE</span>
          </span>
        </div>
      </div>

      <div
        className={`transition-colors duration-300 border-b ${
          scrolled || open
            ? "bg-navy-900/95 backdrop-blur-md border-gold-500/25 shadow-lg shadow-navy-950/40"
            : "bg-navy-900/80 backdrop-blur-md border-gold-500/15"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" aria-label="United States Federal Agencies home" data-testid="header-home-link">
            <LogoLockup dark compact />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-[13px] font-medium tracking-wide transition-colors duration-200 ${
                    isActive ? "text-gold-300" : "text-slate-200 hover:text-gold-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={goEnroll}
              data-testid="header-enroll-cta"
              className="ml-2 px-5 h-10 rounded-full bg-gold-500 text-navy-900 text-[13px] font-semibold tracking-wide transition-all duration-200 hover:bg-gold-400 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gold-500/25"
            >
              Enroll
            </button>
          </nav>

          <button
            className="lg:hidden text-white p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-navy-950/98 backdrop-blur-xl border-b border-gold-500/25"
            data-testid="nav-drawer"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-3 border-b border-white/5 font-serif text-xl ${
                        isActive ? "text-gold-300" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <button
                onClick={goEnroll}
                data-testid="mobile-enroll-cta"
                className="mt-5 h-12 rounded-full bg-gold-500 text-navy-900 font-semibold tracking-wide"
              >
                Enroll Now
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
